import type { BadgeProps, BadgeTone } from './Badge.types'

/**
 * badge — inline status/category tag primitive.
 *
 * Fully controlled and stateless: appearance chosen solely from `tone`
 * (1:1 tone → treatment, all colors from theme tokens), children rendered
 * verbatim inside one inline tag that keeps content flow.
 *
 * Four families, and picking between them is the whole design decision:
 *
 * - QUIET carries no fill — the tone for a merely informational value. A column
 *   where every tag is filled says nothing; quiet makes the filled ones mean
 *   something.
 * - STATUS tints (neutral/info/accent/success/warning/danger) pair a tint
 *   background with that status's text-safe tone, the theme's status trio. The
 *   signal colours are never a fill under light text.
 * - ACTION rungs are the solid action-ladder fills under a white label — the
 *   same colours the buttons use, so "you must do something" reads at the weight
 *   of the control that does it. `action` has time to spare, `action-urgent` is
 *   the deadline closing in, `action-critical` is the deadline gone.
 * - INK tones carry those colours in the TEXT instead. Same meaning, a fraction
 *   of the weight: pick ink when the tag repeats down a list, the fill when it
 *   appears once beside its control. The ACTION inks are semibold because they
 *   still demand something; the STATUS inks only report.
 *
 * The geometry is identical across all four, so filled, ink and quiet values in
 * one column still line up.
 */
const tones: Record<BadgeTone, string> = {
  quiet: 'text-text',
  neutral: 'bg-surface-muted text-text-muted',
  info: 'bg-info-surface text-info-strong border-info-edge',
  accent: 'bg-accent-muted text-accent',
  success: 'bg-success-surface text-success-strong border-success-edge',
  warning: 'bg-warning-surface text-warning-strong border-warning-edge',
  danger: 'bg-danger-surface text-danger-strong border-danger-edge',
  action: 'bg-action-primary text-action-contrast',
  'action-urgent': 'bg-action-urgent text-action-contrast',
  'action-critical': 'bg-action-critical text-action-contrast',
  'action-ink': 'font-semibold text-action-primary',
  'action-urgent-ink': 'font-semibold text-action-urgent',
  'action-critical-ink': 'font-semibold text-action-critical',
  'success-ink': 'text-success-strong',
  'warning-ink': 'text-warning-strong',
  'danger-ink': 'text-danger-strong',
}

export function Badge({ tone = 'neutral', children }: BadgeProps) {
  return (
    <span
      // EVERY BADGE CARRIES THE 1px, whether or not it is painted. A tinted
      // badge needs a stroke to be a shape at all — its fill reaches only
      // 1.02–1.23:1 against the plane behind it, so today the text draws the
      // whole thing. The four status tones name their `-edge`; every other tone
      // leaves it transparent. The width is here rather than in the tones so
      // that a filled badge and a tinted one are still the same size, which the
      // note above promises.
      className={`inline-flex items-center rounded-sm border border-transparent px-2 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  )
}
